import { Field, Input, Text } from '@chakra-ui/react';
import { Control, Controller, FieldErrors, FieldValues, Path } from 'react-hook-form';
import { ChangeEvent } from 'react';

interface FormPersonnummerInputProps<T extends FieldValues = FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
}

// Format personnummer for display (adds hyphen if needed)
const formatPersonnummer = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // If we have more than 8 digits, add hyphen after 8th digit
  if (digits.length > 8) {
    return `${digits.slice(0, 8)}-${digits.slice(8, 12)}`;
  }

  return digits;
};

// Clean personnummer (remove all non-digit characters) for storage
const cleanPersonnummer = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Custom Personnummer input component that:
 * - Accepts only numeric input (0-9 and hyphen for formatting)
 * - Formats display with hyphen (YYYYMMDD-XXXX)
 * - Stores value as clean digits (removes hyphen)
 * - Works with react-hook-form Controller
 * - Validates Swedish Personnummer format (12 digits)
 */
const FormPersonnummerInput = <T extends FieldValues = FieldValues>({
  name,
  label,
  control,
  errors,
  required = false,
  placeholder = 'ÅÅÅÅMMDD-XXXX',
  helperText,
}: FormPersonnummerInputProps<T>) => {
  const fieldError = errors[name];
  const isInvalid = !!fieldError;

  return (
    <Field.Root invalid={isInvalid}>
      <Field.Label>
        {label}
        {required && <Text color="red.fg">*</Text>}
      </Field.Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;

            // Only allow digits and hyphen
            const sanitized = inputValue.replace(/[^\d-]/g, '');

            // Store clean digits only (no hyphen)
            const cleaned = cleanPersonnummer(sanitized);
            field.onChange(cleaned);
          };

          const handleBlur = () => {
            field.onBlur();
          };

          // Format the stored value for display
          const displayValue = formatPersonnummer(field.value || '');

          return (
            <Input
              value={displayValue}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              maxLength={13} // YYYYMMDD-XXXX = 13 characters
              inputMode="numeric"
            />
          );
        }}
      />
      {helperText && <Field.HelperText>{helperText}</Field.HelperText>}
      {isInvalid && <Field.ErrorText>{fieldError.message?.toString()}</Field.ErrorText>}
    </Field.Root>
  );
};

export default FormPersonnummerInput;
